import { ComponentType, FC, PropsWithChildren, useMemo } from 'react';

import { PaletteMode, ThemeProvider, useTheme } from '@mui/material';
import { DARK_CLASS, LIGHT_CLASS } from '@natter-co/design-system';
import { theme as createTheme, useInheritedVisualRefresh } from 'common/theme';

type Props = {
  mode?: PaletteMode;
};

/**
 * Bridges MUI's dark mode to the Design System 2.0 colour scheme.
 *
 * The two express "this region is dark" in incompatible ways: MUI through a React context scoped to
 * a subtree, DS 2.0 through a class on an ancestor element (`components/Theme`). A refreshed
 * component inside a `mode="dark"` provider therefore cannot see the mode at all, and draws its
 * LIGHT ramp — a `Color/Light/2` button sitting on the `Color/Dark/3` control bar. Emitting the
 * class alongside the theme is what keeps the two worlds in step wherever either is used.
 *
 * `display: contents` so this adds a class hook and nothing else: no box, no layout, no spacing.
 *
 * Both directions are marked, and the light one matters more than it looks. A `mode="light"` provider
 * nested inside a dark region cannot un-match an ancestor `.dark` in CSS — the variant is written
 * `:where(.dark) &`, and a light counterpart would carry equal specificity, so source order would
 * decide globally instead of proximity deciding locally. `LIGHT_CLASS` is therefore a marker for
 * `useInheritedDarkMode` to STOP at rather than a style hook; no stylesheet matches it.
 *
 * That used to be unnecessary: every such nesting is a modal, and a modal portalled out of the
 * subtree and escaped the class on its way to `<body>`. `Dialog` now carries the ambient scheme
 * across the portal on purpose, so the escape is gone and the light region has to declare itself —
 * otherwise the seven light-inside-dark modals (`StartNatteringContainer`, `EndEvent`,
 * `RecordingButton`, …) get a dark DS surface wrapped around light MUI content.
 */
const DsColorScheme = ({
  children,
  mode,
  visualRefresh,
}: PropsWithChildren<Required<Props> & { visualRefresh: boolean }>) =>
  visualRefresh ? (
    <div className={mode === 'dark' ? DARK_CLASS : LIGHT_CLASS} style={{ display: 'contents' }}>
      {children}
    </div>
  ) : (
    children
  );

export const NatterThemeProvider = ({ children, mode = 'light' }: PropsWithChildren<Props>) => {
  const visualRefresh = useInheritedVisualRefresh();
  const theme = useMemo(() => createTheme(undefined, mode, visualRefresh), [mode, visualRefresh]);

  return (
    <ThemeProvider theme={theme}>
      <DsColorScheme mode={mode} visualRefresh={visualRefresh}>
        {children}
      </DsColorScheme>
    </ThemeProvider>
  );
};

export const NatterDarkThemeProvider = ({ children }: PropsWithChildren) => {
  const { palette } = useTheme();
  const visualRefresh = useInheritedVisualRefresh();

  return (
    <ThemeProvider theme={{ palette: { ...palette, mode: 'dark' } }}>
      <DsColorScheme mode="dark" visualRefresh={visualRefresh}>
        {children}
      </DsColorScheme>
    </ThemeProvider>
  );
};

/**
 * Switches a subtree to the dark theme once the Design System 2.0 refresh is on, and leaves it
 * exactly as it was otherwise (HNS-263).
 *
 * The live-room side panels are white today and `Color/Dark/3` under the refresh. Every colour in
 * them beyond that one background comes off the theme, so flipping the mode is the change — the
 * panels themselves only stop pinning `common.white`. Retiring the flag means replacing each usage
 * with `NatterThemeProvider mode="dark"` and deleting this.
 */
export const RefreshDarkThemeProvider = ({ children }: PropsWithChildren) => {
  // Inherited rather than read from the constant (see `useInheritedVisualRefresh`): these are
  // exactly the panels whose stories the toolbar needs to be able to flip.
  const visualRefresh = useInheritedVisualRefresh();

  return visualRefresh ? <NatterThemeProvider mode="dark">{children}</NatterThemeProvider> : children;
};

export const withTheme =
  (config: Props) =>
  <P extends object>(WrappedComponent: ComponentType<P>): FC<P> =>
  (props) => (
    <NatterThemeProvider {...config}>
      <WrappedComponent {...props} />
    </NatterThemeProvider>
  );
